import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
// Remove Auth0Provider import from here
// import { Auth0Provider } from '@auth0/auth0-react'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
// Initialize Sentry before any other imports
import { initSentry } from './config/sentry';
initSentry();

// Initialize Immer plugins before any stores are used
import { enableMapSet } from 'immer';
enableMapSet();

// SECURITY: Import cache security utilities for secure persistence
import { CacheSecurityManager } from './utils/cacheSecurityManager';

// PWA status bar enhancements
import './styles/pwa-enhancements.css';

// --- Chart.js Global Setup ---
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale, // Ensure TimeScale is imported
  ArcElement, // Added ArcElement
  BarElement  // Added BarElement
} from 'chart.js';
import 'chartjs-adapter-date-fns'; // Side-effect import for date adapter
import { CandlestickController, CandlestickElement, OhlcController, OhlcElement } from 'chartjs-chart-financial';
import ChartAnnotationsPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register ALL components/plugins needed globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  CandlestickController,
  OhlcController,
  CandlestickElement,
  OhlcElement,
  ChartAnnotationsPlugin,
  zoomPlugin,
  ArcElement, // Added ArcElement
  BarElement  // Added BarElement
);
// --- End Chart.js Global Setup ---

import App from './App.tsx'
import ScrollToTop from './components/ScrollToTop';
import Auth0ProviderWithNavigate from './components/Auth0ProviderWithNavigate'; // Import the new wrapper
import './index.css'

console.log('Application starting...');
console.log('Chart.js components registered globally.'); // Add log

// Log any unhandled errors to help with debugging, but filter out browser extension errors
window.addEventListener('error', (event) => {
  // Filter out browser extension errors that we cannot control
  const errorMessage = event.message || '';
  const isExtensionError = 
    errorMessage.includes('Could not establish connection') ||
    errorMessage.includes('Receiving end does not exist') ||
    errorMessage.includes('Extension context invalidated') ||
    errorMessage.includes('chrome-extension:') ||
    errorMessage.includes('moz-extension:') ||
    event.filename?.includes('extension://') ||
    event.filename?.includes('chrome-extension://') ||
    event.filename?.includes('moz-extension://');
    
  if (isExtensionError) {
    // Silently ignore browser extension errors - they don't affect our app
    console.debug('[Extension Error - Ignored]:', errorMessage);
    return;
  }
  
  console.error('Unhandled error:', event.error);
  document.body.innerHTML = `<div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 50px auto; border: 1px solid #f88; background-color: #fee; border-radius: 8px;"><h1>Error Loading Application</h1><p>${errorMessage || 'An unknown error occurred'}</p><p>Check the browser console for more details.</p></div>`;
});

// Also handle unhandled promise rejections from extensions
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.message || event.reason || '';
  const stack = event.reason?.stack || '';
  
  const isExtensionError = 
    reason.includes && (
      reason.includes('Could not establish connection') ||
      reason.includes('Receiving end does not exist') ||
      reason.includes('Extension context invalidated') ||
      reason.includes('chrome-extension:') ||
      reason.includes('moz-extension:') ||
      reason.includes('browser extension') ||
      reason.includes('content script') ||
      stack.includes('chrome-extension://') ||
      stack.includes('moz-extension://')
    );
    
  if (isExtensionError) {
    // Prevent the unhandled rejection from showing in console
    event.preventDefault();
    console.debug('[Extension Promise Rejection - Ignored]:', reason);
    return;
  }
  
  console.warn('Unhandled promise rejection:', event.reason);
});

// Auth0 configuration from environment variables
// In production, these should be set as environment variables
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN || 'auth.botblender.com'
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '0sSe2z0bYqUmz5P3BqLkyIv1xMvwqFcF'
const auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE || 'https://trade-dashboard-api'

// Google reCAPTCHA configuration
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

console.log('Auth0 config:', { 
  domain: auth0Domain, 
  clientId: auth0ClientId ? auth0ClientId.substring(0, 3) + '...' : 'Not set',
  audience: auth0Audience 
});

// Check if Auth0 config is valid
if (!auth0Domain || !auth0ClientId) {
  console.error('Auth0 configuration is missing. Please check your .env file.');
  document.body.innerHTML = `<div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 50px auto; border: 1px solid #f88; background-color: #fee; border-radius: 8px;"><h1>Error Loading Application</h1><p>Auth0 configuration is missing. Please check your .env file.</p></div>`;
}

// Create a client instance for React Query with login-optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // More conservative stale times to prevent rapid refetches during login
      staleTime: 2 * 60 * 1000, // 2 minutes
      // Keep data in cache for shorter time to prevent stale auth data
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      // Prevent automatic refetching during initial login flow
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Reduce retry attempts to prevent query storms during login
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      // Enable network mode to handle offline scenarios
      networkMode: 'online',
    },
  },
  // Add enhanced error handling to prevent React Error #300
  queryCache: new QueryCache({
    onError: (error: any, query: any) => {
      // Prevent error propagation during initial auth/login flow
      if (query.state.fetchStatus === 'fetching' && 
          (query.queryKey.includes('pnlTimeframes') || 
           query.queryKey.includes('accountSummary') ||
           query.queryKey.includes('strategySummary'))) {
        console.warn('[QueryClient] Suppressing error during initial login fetch:', query.queryKey);
        return;
      }
      console.error('[QueryClient] Query error:', error, query.queryKey);
    },
    // Removed onSuccess callback to prevent React Error #300
    // The global onSuccess callback was causing state updates during render cycles
    // especially during authentication transitions
  }),
});

// SECURITY: User-aware cache persistence
const CACHE_KEY_PREFIX = 'trade-dashboard-query-cache';
let currentUserId: string | null = null;
let cacheSecurityManager: CacheSecurityManager | null = null;

// Type for cached query data
interface CachedQuery {
  queryKey: readonly unknown[];
  data: unknown;
  dataUpdatedAt: number;
  userId?: string; // SECURITY: Track which user the cache belongs to
}

// SECURITY: Get user-specific cache key
const getUserCacheKey = (userId: string): string => {
  return `${CACHE_KEY_PREFIX}-${userId}`;
};

// SECURITY: Initialize cache security manager
export const initializeCacheSecurity = (userId: string, securityManager: CacheSecurityManager) => {
  currentUserId = userId;
  cacheSecurityManager = securityManager;
  console.log(`🔐 [Cache Persistence] Initialized for user: ${userId.substring(0, 20)}...`);
};

// SECURITY: Clear cache security context (on logout)
export const clearCacheSecurity = () => {
  console.log('🚪 [Cache Persistence] Clearing user context');
  currentUserId = null;
  cacheSecurityManager = null;
};

// SECURITY: Manually trigger secure cache restoration
export const restoreCacheForUser = () => {
  if (currentUserId && cacheSecurityManager) {
    console.log(`♻️ [Cache Persistence] Manually restoring cache for user: ${currentUserId.substring(0, 20)}...`);
    restoreCache();
  } else {
    console.warn('⚠️ [Cache Persistence] Cannot restore - no user context');
  }
};

// SECURITY: Save cache to localStorage with user validation
const persistCache = () => {
  if (!currentUserId || !cacheSecurityManager) {
    console.warn('⚠️ [Cache Persistence] Cannot persist - no user context');
    return;
  }

  try {
    const userCacheKey = getUserCacheKey(currentUserId);
    const cacheData: CachedQuery[] = queryClient.getQueryCache().getAll()
      .filter(query => {
        // Only persist user-aware cache entries
        const keyString = JSON.stringify(query.queryKey);
        
        // EXCLUDE feature-flags from persistence - they should always reflect current Auth0 roles
        if (keyString.includes('feature-flags')) {
          return false;
        }
        
        return keyString.includes(currentUserId!);
      })
      .map(query => ({
        queryKey: query.queryKey,
        data: query.state.data,
        dataUpdatedAt: query.state.dataUpdatedAt,
        userId: currentUserId!, // Mark cache with user ID
      }));
    
    localStorage.setItem(userCacheKey, JSON.stringify(cacheData));
    console.log(`💾 [Cache Persistence] Persisted ${cacheData.length} queries for user: ${currentUserId!.substring(0, 20)}...`);
    // Reduced logging frequency for cache persist
  } catch (error) {
    console.warn('[QueryClient] Failed to persist cache:', error);
  }
};

// PWA-aware cache quality validation
const validateCacheQuality = (queryKey: readonly unknown[], data: unknown): boolean => {
  if (!data) return false;
  
  // Validate specific query types for meaningful data
  const keyString = JSON.stringify(queryKey);
  
  if (keyString.includes('accountSummary')) {
    return Array.isArray(data) && data.length > 0;
  }
  
  if (keyString.includes('pnlTimeframes') || keyString.includes('strategySummary')) {
    return data && typeof data === 'object' && Object.keys(data).length > 0;
  }
  
  if (keyString.includes('trades') || keyString.includes('balanceHistory')) {
    return Array.isArray(data) && data.length > 0;
  }
  
  // For other queries, just check if data exists and isn't empty
  if (Array.isArray(data)) {
    return data.length > 0;
  }
  
  if (typeof data === 'object' && data !== null) {
    return Object.keys(data).length > 0;
  }
  
  return data != null;
};

// Detect PWA mode and memory pressure (iOS Safari specific)
const detectPWAEnvironment = () => {
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
               // @ts-ignore - iOS Safari specific
               window.navigator.standalone === true;
  
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                     !(window as any).MSStream; // Exclude Internet Explorer on Windows Phone
  
  let hasMemoryPressure = false;
  try {
    // @ts-ignore - Check memory if available
    if (window.performance && window.performance.memory) {
      // @ts-ignore
      const { usedJSHeapSize, totalJSHeapSize } = window.performance.memory;
      hasMemoryPressure = totalJSHeapSize > 0 && (usedJSHeapSize / totalJSHeapSize) > 0.8;
    }
  } catch (e) {
    // Memory API not available, assume no pressure
  }
  
  return { isPWA, isIOSSafari, hasMemoryPressure };
};

// SECURITY: Restore cache from localStorage with user validation - PWA-aware with quality validation
const restoreCache = () => {
  if (!currentUserId || !cacheSecurityManager) {
    console.log('ℹ️ [Cache Restoration] Skipping - waiting for user authentication');
    return;
  }

  const { isPWA, isIOSSafari, hasMemoryPressure } = detectPWAEnvironment();
  let meaningfulCacheRestored = false;
  
  try {
    const userCacheKey = getUserCacheKey(currentUserId);
    const cached = localStorage.getItem(userCacheKey);
    if (cached) {
      const cacheData: CachedQuery[] = JSON.parse(cached);
      const now = Date.now();
      
      // More conservative cache age for PWA/iOS Safari
      const maxCacheAge = (isPWA || isIOSSafari) ? 15 * 60 * 1000 : 30 * 60 * 1000; // 15min vs 30min
      
      cacheData.forEach(({ queryKey, data, dataUpdatedAt, userId: cacheUserId }: CachedQuery) => {
        // SECURITY: Validate that cached data belongs to current user
        if (cacheUserId && cacheUserId !== currentUserId) {
          console.error('🚨 [Security] Cache restoration blocked - user mismatch', {
            currentUser: currentUserId!.substring(0, 20) + '...',
            cacheUser: cacheUserId.substring(0, 20) + '...',
            queryKey
          });
          return;
        }

        // Check cache age and validate quality
        if (data && (now - dataUpdatedAt) < maxCacheAge) {
          try {
            // Validate cache quality before restoring
            if (validateCacheQuality(queryKey, data)) {
              queryClient.setQueryData(queryKey, data);
              meaningfulCacheRestored = true;
              console.log('[Cache Restoration] Restored quality cache for:', queryKey);
            } else {
              console.log('[Cache Restoration] Skipped low-quality cache for:', queryKey);
            }
          } catch (queryError) {
            console.warn('[QueryClient] Failed to restore specific query:', queryKey, queryError);
          }
        } else {
          console.log('[QueryClient] Skipped stale cache for:', queryKey);
        }
      });
      
      // Log PWA-specific cache restoration info
      if (isPWA || isIOSSafari) {
        console.log('[QueryClient] PWA Cache Info:', {
          isPWA,
          isIOSSafari,
          hasMemoryPressure,
          meaningfulCacheRestored,
          totalCacheEntries: cacheData.length
        });
      }
    }
  } catch (error) {
    console.warn('[QueryClient] Failed to restore cache:', error);
  }
  
  // Store cache quality info for other components to use
  try {
    localStorage.setItem('td_cache_quality', JSON.stringify({
      hasMeaningfulCache: meaningfulCacheRestored,
      isPWAMode: isPWA,
      isIOSSafari: isIOSSafari,
      hasMemoryPressure: hasMemoryPressure,
      restoredAt: Date.now()
    }));
  } catch (e) {
    // Ignore localStorage errors
  }
  
  // FIX: Don't mark cache restoration as complete here - wait for auth to stabilize in App.tsx
  // This prevents premature loading state transitions before authentication is ready
  // useUiStore.getState().setCacheRestorationComplete(); // REMOVED - moved to App.tsx
};

// Restore cache SYNCHRONOUSLY before React renders to avoid loading screen flash
restoreCache();

// SECURITY: Debug function for clearing user-specific cache (available in browser console)
(window as any).clearAppCache = () => {
  console.log('🧹 Clearing app cache...');
  
  // Clear user-specific cache if user context is available
  if (currentUserId) {
    const userCacheKey = getUserCacheKey(currentUserId);
    localStorage.removeItem(userCacheKey);
    console.log(`🧹 Cleared cache for user: ${currentUserId.substring(0, 20)}...`);
  }
  
  // Clear all legacy cache keys as fallback
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_KEY_PREFIX)) {
      localStorage.removeItem(key);
    }
  });
  
  queryClient.clear();
  console.log('✅ Cache cleared! Reloading...');
  window.location.reload();
};

// Persist cache every 10 seconds and on page unload
setInterval(persistCache, 10000);
window.addEventListener('beforeunload', persistCache);

// Ensure DOM is fully loaded before trying to render React
const renderApp = () => {
  const rootElement = document.getElementById('root');

  // Add device detection classes to body for CSS targeting
  const addDeviceClasses = () => {
    // Check for touch capability
    const isTouchDevice = 'ontouchstart' in window || 
                         navigator.maxTouchPoints > 0 ||
                         (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
    
    // Check user agent for mobile patterns
    const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    
    // Add class to body for CSS targeting
    if (isTouchDevice || isMobileUserAgent) {
      document.body.classList.add('is-mobile-device');
      console.log('[DeviceDetection] Added is-mobile-device class to body');
    } else {
      document.body.classList.add('is-desktop-device');
      console.log('[DeviceDetection] Added is-desktop-device class to body');
    }
  };

  // Run device detection
  addDeviceClasses();

  if (rootElement) {
    try {
      // Check if Auth0 configuration looks valid (optional redundancy)
      const auth0ConfigValid =
        auth0Domain && auth0ClientId && auth0Audience &&
        auth0Domain !== 'your-auth0-domain.auth0.com' &&
        auth0ClientId !== 'your-auth0-client-id' &&
        auth0Audience !== 'https://api.trading-dashboard.com' &&
        auth0Domain.includes('.') &&
        auth0ClientId.length > 5;

      // Create React root
      const root = ReactDOM.createRoot(rootElement);

      // Add the react-ready class immediately to enable React content visibility
      // This removes the CSS-based loading overlay as soon as React takes over
      rootElement.classList.add('react-ready');
      console.log('[Main] Added react-ready class to enable React content visibility');

      if (auth0ConfigValid) {
        console.log('Rendering app with Auth0 configuration via wrapper');
        // Render normal application with Auth0
        root.render(
          // Wrap with QueryClientProvider first
          <QueryClientProvider client={queryClient}>
            {/* Google reCAPTCHA Provider for bot protection */}
            <GoogleReCaptchaProvider
              reCaptchaKey={recaptchaSiteKey}
              scriptProps={{
                async: true,
                defer: true,
                appendTo: 'head',
              }}
            >
              {/* BrowserRouter now wraps Auth0ProviderWithNavigate */}
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Auth0ProviderWithNavigate>
                  <ScrollToTop /> {/* ScrollToTop can be here or inside App */}
                  <App />
                </Auth0ProviderWithNavigate>
              </BrowserRouter>
            </GoogleReCaptchaProvider>
          </QueryClientProvider>
        );
      } else {
        console.error('Auth0 configuration appears invalid. Showing fallback UI.');
        // Display minimal UI with error message
        rootElement.innerHTML = `
          <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 50px auto; border: 1px solid #ddd; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <h1>Bybit Trading Dashboard</h1>
            <div style="padding: 15px; background-color: #f8d7da; color: #721c24; border-radius: 5px; margin: 20px 0;">
              <h2>Auth0 Configuration Issue</h2>
              <p>The application could not initialize properly due to missing or invalid Auth0 credentials.</p>
            </div>
            <h3>Required Configuration:</h3>
            <p>Make sure your .env file contains valid Auth0 credentials:</p>
            <pre style="background-color: #f5f5f5; padding: 10px; border-radius: 5px;">
              VITE_AUTH0_DOMAIN=your-auth0-domain.auth0.com
              VITE_AUTH0_CLIENT_ID=your-auth0-client-id
              VITE_AUTH0_AUDIENCE=your-auth0-audience
            </pre>
            <p>Current configuration (may be using fallback values):</p>
            <ul>
              <li>Domain: ${auth0Domain}</li>
              <li>Client ID: ${auth0ClientId ? auth0ClientId.substring(0, 3) + '...' : 'Not set'}</li>
              <li>Audience: ${auth0Audience}</li>
            </ul>
          </div>
        `;
      }
    } catch (error) {
      console.error('Error rendering app:', error);
      // Provide minimal error message UI
      rootElement.innerHTML = `
        <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 600px; margin: 50px auto; border: 1px solid #f88; background-color: #fee; border-radius: 8px;">
          <h1>Error Loading Application</h1>
          <p>${error instanceof Error ? error.message : 'An unknown error occurred'}</p>
          <p>Check the browser console for more details.</p>
        </div>
      `;
    }
  } else {
    console.error('Root element not found');
    // Last resort - try to add root element if it doesn't exist
    const newRoot = document.createElement('div');
    newRoot.id = 'root';
    document.body.appendChild(newRoot);
    console.log('Created new root element, attempting to render again...');
    // Try again after a brief delay
    setTimeout(renderApp, 100);
  }
};

// If document is already complete, render immediately, otherwise wait for DOMContentLoaded
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  renderApp();
} else {
  document.addEventListener('DOMContentLoaded', renderApp);
}